import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  DiscriminatedInspectionEvaluationModel,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const PropsModel = ProjectModel.pick({
  fiscalYear: true,
  sheetType: true,
  inspectionEvaluation: true,
});

type Props = z.infer<typeof PropsModel>;

export const SsInspectionEvaluation: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { id: projectId } = useId();

  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(props);

  if (!parsed.success) {
    return null;
  }

  const { inspectionAndImprovementByAdministrativeJurisdiction } =
    parsed.data.inspectionEvaluation;

  return (
    <ContentWrapper
      title={t('inspection-and-improvement-by-iaa-administrative-jurisdiction')}
      borderBottom={0}
    >
      <ContentItem paddingBottom={0} borderBottom={0}>
        <ContentItems
          style={{ marginBottom: 24 }}
          items={[
            <PinpointMarker
              key="inspection-result"
              pinpoint={{
                path: { project: projectId },
                field: 'ss.inspection-result',
              }}
            >
              <TitledText
                title={t('inspection-result')}
                text={
                  inspectionAndImprovementByAdministrativeJurisdiction?.inspectionResult
                }
              />
            </PinpointMarker>,
            <PinpointMarker
              key="evaluation"
              pinpoint={{
                path: { project: projectId },
                field: 'ss.evaluation',
              }}
            >
              <TitledText
                title={t('evaluation')}
                text={
                  inspectionAndImprovementByAdministrativeJurisdiction?.evaluation
                }
              />
            </PinpointMarker>,
          ]}
        />
      </ContentItem>

      <ContentItems
        items={[
          <PinpointMarker
            key="direction-of-improvement"
            pinpoint={{
              path: { project: projectId },
              field: 'ss.direction-of-improvement',
            }}
          >
            <TitledText
              title={t('direction-of-improvement')}
              text={
                inspectionAndImprovementByAdministrativeJurisdiction?.directionOfImprovement
              }
            />
          </PinpointMarker>,
        ]}
      />
    </ContentWrapper>
  );
};
